import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  JsonExtractPath,
  Provider,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { addColumns } from '../utils/schemas'
import { SelectWorkflowComponent } from './common/selectcomponent'

class ParseJSON extends SelectWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.parsejson',
        group: 'Data Preparation',
        title: 'Parse JSON',
        description: `
      This component creates new columns with values extracted from the JSON string in other columns.
      It uses the Data Warehouse syntax to specify the path to the key that needs to be extracted.
      `
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'newcolumns',
          type: WorkflowComponentParamType.JsonExtractPaths,
          title: 'New columns',
          parent: 'source'
        }
      ]
    )
  }

  getInnerSql({ params, connection }: WorkflowComponentCodeParams) {
    const preparedParams = this._prepareParameters(params)
    const paths = preparedParams.newcolumns as string
    // paths is a json string with the following format:
    // [
    //  {
    //      "jsoncolumn": "mycolumn",
    //      "path": "$.mykey"
    //      "newcol": "mynewcol"
    //  },
    //  {
    //      "jsoncolumn": "mycolumn2",
    //      "path": "$.mykey2"
    //      "newcol": "mynewcol2"
    //  },
    //  ...
    // ]
    let pathsObjs: JsonExtractPath[]
    try {
      pathsObjs = JSON.parse(paths) as JsonExtractPath[]
    } catch (error) {
      pathsObjs = []
    }
    const newcolumns: string[] = []
    for (const pathObj of pathsObjs) {
      const path = `'${pathObj.path}'`
      const newcol = pathObj.newcol
      const jsoncolumn = pathObj.jsoncolumn
      let sql = ''
      switch (connection.type) {
        case Provider.BigQuery:
          sql = dedent`
                JSON_QUERY(
                    ${jsoncolumn},
                    ${path}
                ) AS ${newcol}`
          break
        case Provider.Snowflake:
          sql = dedent`
                JSON_EXTRACT_PATH_TEXT(
                    ${jsoncolumn},
                    ${path}
                ) AS ${newcol}`
          break
        case Provider.Postgres:
          sql = dedent`
                JSONB_PATH_QUERY(
                    ${jsoncolumn}::jsonb,
                    ${path}::jsonpath
                ) AS ${newcol}`
          break
        case Provider.Redshift:
          sql = dedent`
                JSON_EXTRACT_PATH_TEXT(
                    ${jsoncolumn},
                    ${path}
                ) AS ${newcol}`
          break
        case Provider.DatabricksRest:
          sql = dedent`
                GET_JSON_OBJECT(
                    ${jsoncolumn},
                    ${path}
                ) AS ${newcol}`
          break
        default:
          sql = ''
          break
      }
      newcolumns.push(sql)
    }
    return dedent`
      SELECT
        *,
        ${newcolumns.join(',\n')}
      FROM
        ${connection.quoteName(params.source as string)}`
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params }: GetOutputSchemaParams): TableSchema {
    const preparedParams = this._prepareParameters(params)
    const paths = preparedParams.newcolumns as string
    let pathsObjs: JsonExtractPath[]
    try {
      pathsObjs = JSON.parse(paths) as JsonExtractPath[]
    } catch (error) {
      pathsObjs = []
    }
    const newcolumns: TableSchema = []
    for (const pathObj of pathsObjs) {
      newcolumns.push({
        name: pathObj.newcol,
        type: ColumnDataType.String
      })
    }
    return addColumns(columns.source, newcolumns)
  }

  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Snowflake, Provider.Postgres, Provider.Redshift, Provider.DatabricksRest]
  }
}

export { ParseJSON }
